/**
 * Adresses d'une entree (`secrets.uris`). Un import (Bitwarden, Enpass, NordPass) ou une fusion de doublons en
 * laisse plusieurs : la fiche les montre toutes et l'editeur les gere toutes -- la premiere reste celle qui donne
 * le domaine affiche dans la liste et la Sante du coffre.
 */

/**
 * Cle `localStorage` de la detection par defaut du coffre (`defaultUriMatchRule` du coeur) : une preference de cet
 * appareil, jamais un secret, relue comme une donnee NON FIABLE (hors choix proposes = domaine).
 */
export const DEFAULT_URI_MATCH_KEY = 'klavox:default-uri-match';

/** Adresses telles qu'enregistrees : rognees, vides retirees, doublons exacts retires, ordre de saisie garde. */
export function normalizeEntryUris(uris: readonly string[]): string[] {
  const kept: string[] = [];
  for (const uri of uris) {
    const trimmed = uri.trim();
    if (trimmed.length > 0 && !kept.includes(trimmed)) kept.push(trimmed);
  }
  return kept;
}

/**
 * Lien a ouvrir pour une adresse, ou `null`. Seuls `http:` et `https:` s'ouvrent : `javascript:`, `data:`,
 * `androidapp://`, `ssh://`... restent du texte. Une adresse sans schema (`exemple.fr/connexion`, forme courante
 * des imports CSV) s'ouvre en `https://` si son hote ressemble a un nom de domaine.
 */
export function openableUri(uri: string): string | null {
  const trimmed = uri.trim();
  if (trimmed.length === 0 || /\s/u.test(trimmed)) return null;
  // Un schema suivi d'autre chose qu'un port (`exemple.fr:8443`) n'est pas une adresse web.
  const hasScheme = /^[a-z][a-z0-9+.-]*:(?!\d)/iu.test(trimmed);
  let url: URL;
  try {
    url = new URL(hasScheme ? trimmed : `https://${trimmed}`);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
  if (!hasScheme && !url.hostname.includes('.') && url.hostname !== 'localhost') return null;
  return url.href;
}

/**
 * Deplace l'adresse de rang `from` d'un cran vers `direction` (-1 = monter, +1 = descendre) dans la liste de
 * l'editeur `[Site (URL), ...supplementaires]`, sa regle de detection avec elle : `rules` est aligne sur `uris`
 * (absente = '' = defaut du coffre), et une regle ne reste jamais sur la ligne qu'une autre adresse vient occuper.
 * Monter la deuxieme adresse en fait le « Site (URL) », celle qui donne le domaine de la liste et de la Sante du
 * coffre. Rang hors liste ou deplacement hors des bornes : `null`, rien ne bouge.
 */
export function moveEntryUri(
  uris: readonly string[],
  rules: readonly string[],
  from: number,
  direction: -1 | 1,
): { uris: string[]; rules: string[] } | null {
  const to = from + direction;
  if (!Number.isInteger(from) || from < 0 || from >= uris.length || to < 0 || to >= uris.length) return null;
  const movedUris = [...uris];
  const movedRules = uris.map((_uri, index) => rules[index] ?? '');
  [movedUris[from], movedUris[to]] = [movedUris[to]!, movedUris[from]!];
  [movedRules[from], movedRules[to]] = [movedRules[to]!, movedRules[from]!];
  return { uris: movedUris, rules: movedRules };
}
